import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"

export type UploadPurpose =
  | "child-photo"
  | "child-document"
  | "child-report"
  | "nomination-attachment"
  | "event-banner"
  | "ability-photo"
  | "ability-document"
  | "ability-medical"

const MAX_UPLOAD_SIZE = 50 * 1024 * 1024
const ALLOWED_TYPES: Record<UploadPurpose, Set<string>> = {
  "child-photo": new Set(["image/jpeg", "image/png", "image/webp"]),
  "child-document": new Set(["application/pdf", "image/jpeg", "image/png"]),
  "child-report": new Set(["application/pdf", "image/jpeg", "image/png"]),
  "nomination-attachment": new Set(["application/pdf", "image/jpeg", "image/png"]),
  "event-banner": new Set(["image/jpeg", "image/png", "image/webp"]),
  "ability-photo": new Set(["image/jpeg", "image/png", "image/webp"]),
  "ability-document": new Set(["application/pdf", "image/jpeg", "image/png"]),
  "ability-medical": new Set(["application/pdf", "image/jpeg", "image/png"]),
}

export interface UploadedFile {
  key: string
  name: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
}

export async function uploadFile(
  file: File,
  purpose: UploadPurpose,
  options?: { onProgress?: (progress: number) => void },
): Promise<UploadedFile> {
  if (file.size > MAX_UPLOAD_SIZE) throw new Error("File must be 50MB or smaller")
  if (!ALLOWED_TYPES[purpose].has(file.type)) throw new Error("This file type is not supported")

  const presignResponse = await client.api.admin.uploads["presigned-url"].$post({
    json: {
      fileName: file.name,
      contentType: file.type,
      size: file.size,
      purpose,
    },
  })
  const presignBody = await presignResponse.json()
  if (!presignResponse.ok || !("data" in presignBody)) {
    throw new Error(getErrorMessage(presignBody))
  }

  if (options?.onProgress) {
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open("PUT", presignBody.data.uploadUrl)
      request.setRequestHeader("Content-Type", file.type)
      request.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          options.onProgress?.(Math.round((event.loaded / event.total) * 100))
        }
      })
      request.addEventListener("load", () => {
        if (request.status >= 200 && request.status < 300) {
          options.onProgress?.(100)
          resolve()
        } else {
          reject(new Error("File upload failed"))
        }
      })
      request.addEventListener("error", () => reject(new Error("File upload failed")))
      request.addEventListener("abort", () => reject(new Error("File upload was cancelled")))
      request.send(file)
    })
  } else {
    const uploadResponse = await fetch(presignBody.data.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    })
    if (!uploadResponse.ok) throw new Error("File upload failed")
  }

  return {
    key: presignBody.data.key,
    name: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    uploadedAt: new Date().toISOString(),
  }
}
